<script setup lang="ts">
import { capitalize } from 'lodash-es'

const route = useRoute()
const slug = route.params.slug[0] as string
const breadcrumbs = useState('breadcrumbs')

const config = useRuntimeConfig()
const siteName = config.public.siteName
const appConfig = useAppConfig()
const blog = appConfig.blog
const canonical = new URL(route.path, config.public.host)
const blogApi = useStoryblokBlog()

const data = await blogApi.getHome()

const state = reactive({
  page: 1,
  articleItems: [] as StoryBlokArticle[],
  tags: data.tags as StoryBlokTagWithContentItem[],
  selectedTag: {} as StoryBlokTagWithContentItem,
  loading: false,
})

state.selectedTag = state.tags?.find(x => x.slug === slug)
breadcrumbs.value = {
  breadcrumbs: [{ slug: `${blog.url}`, title: capitalize(blog.url.substring(1)) }],
  title: state.selectedTag?.name || 'Tag',
}

// HEAD
const { metaDescription, metaTags } = state.selectedTag.content
const metaDescriptionTag = { name: 'description', content: metaDescription }
const meta = metaTags?.map(({ name, content }) => ({ name, content })) || []

useHead({
  title: `${state.selectedTag?.name} | ${siteName}`,
  meta: [metaDescriptionTag, ...meta],
  link: [{
    rel: 'canonical',
    href: canonical.origin + canonical.pathname,
  }],
})

async function fetchMore() {
  state.loading = true
  const newArticles = await blogApi.getArticles(state.page, { tags: { in_array: state.selectedTag.uuid } })

  newArticles && state.articleItems.push(...newArticles)
  state.page++
  state.loading = false
}

fetchMore()
onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
})
</script>

<template>
  <BlogWrapper :blog-homepage-items="data.homePage.content">
    <div class="flex items-center pb-5">
      <span class="headline-1"># </span><h1>{{ state.selectedTag?.name }}</h1>
    </div>

    <div class="flex overflow-x-auto -mx-5 pl-5 md:flex-wrap md:overflow-hidden md:pl-0 md:-mx-0 gap-2.5 py-5 border-b border-background-secondary mb-5">
      <StoryblokLink
        v-for="tag in state.tags"
        class="text-primary flex-shrink-0 flex items-center text-sm bg-primary bg-opacity-15 px-4 py-2 rounded-lg cursor-pointer transition hover:bg-opacity-25 hover:text-hover"
        :class="{ 'bg-opacity-45 text-hover': slug === tag.slug }"
        :to="`${blog.url}/tag/${tag.slug}`"
      >
        #{{ tag.name }}
      </StoryblokLink>
    </div>
    <BlogRightPanelWrapper
      :top-articles="data.homePage.content.topArticles"
      :most-read-articles="data.homePage.content.mostReadArticles"
      :nepodcento="data.homePage.content.nepodcento[0]"
    >
      <BlogArticleItem
        v-for="(item, zz) in state.articleItems"
        :key="zz + item.slug"
        :item="item"
      />
      <div class="flex justify-center">
        <div v-if="state.loading" class="inline-flex justify-center items-center text-center text-primary text-2xl">
          <AppIcon type="fas" icon="spinner" class="fa-spin mr-2" />
        </div>
        <BaseButton v-else render="secondary" @click="fetchMore">
          {{ blog.fetchMore }}
        </BaseButton>
      </div>
    </BlogRightPanelWrapper>
    <StoryblokProductTiles
      v-editable="data.homePage.content.tiles[0].reference.content"
      v-bind="data.homePage.content.tiles[0].reference.content"
      class="py-70px md:py-140px"
    />
  </BlogWrapper>
</template>
